// ============================================================
// Der einzige Weg zum Backend.
//
// Vier Festlegungen stecken darin, und alle vier haben einen Grund:
//
// 1. RELATIVE Adresse unter /api. Traefik führt /api auf das Backend; bei
//    `pnpm dev` tut dasselbe der Proxy in vite.config.ts (und schneidet das
//    Präfix ab, weil FastAPI seine Routen an der Wurzel führt). Ein absoluter
//    Host stünde in der gebauten Datei und wäre in der zweiten Umgebung
//    falsch — es gibt hier bewusst kein VITE_API_URL.
// 2. cache: "no-store". Eine Rangliste, die aus dem Browsercache kommt, ist
//    keine Rangliste. Ohne die Zeile wendet der Browser auf Antworten ohne
//    Cache-Control eine GESCHÄTZTE Haltbarkeit aus Last-Modified an.
// 3. credentials: "same-origin". Das Sitzungscookie zp_sitzung ist
//    SameSite=Strict und HttpOnly; JavaScript sieht es nie, es muss aber
//    mitgeschickt werden. "include" wäre hier falsch verstandene
//    Großzügigkeit — es gibt keinen fremden Ursprung, der uns etwas angeht.
// 4. Kopfzeile `X-ZP: 1` auf jeder VERÄNDERNDEN Anfrage. Das ist der
//    CSRF-Riegel des Hauses (DECISIONS 4): ein Formular von fremdem Grund
//    kann eigene Kopfzeilen nicht setzen, ohne eine Vorabanfrage auszulösen —
//    und die scheitert, weil das Backend kein CORS spricht. Das Backend weist
//    jede verändernde Anfrage ohne diese Kopfzeile ab.
// ============================================================

const BASIS = "/api";

/** Methoden, die etwas ändern — sie bekommen den CSRF-Riegel. */
const VERAENDERND = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export class ApiError extends Error {
  /** HTTP-Status; 0 bedeutet: die Anfrage kam gar nicht erst an (Netz, Abbruch). */
  readonly status: number;
  /** der ausgewertete Antwortkörper, falls es einen gab — für Feldfehler u. Ä. */
  readonly body: unknown;
  /** die aufgerufene Adresse, damit eine Meldung ohne Zusatzarbeit brauchbar ist */
  readonly url: string;

  constructor(status: number, message: string, body: unknown, url: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.url = url;
  }

  /** true bei „nicht angemeldet" — der Aufrufer schickt dann zur Anmeldung. */
  get istAbgemeldet(): boolean {
    return this.status === 401;
  }
}

/** Antwort kam an, war aber kein JSON — also von etwas anderem als unserem Backend. */
const NICHT_ERREICHBAR = "Server nicht erreichbar — bitte neu laden.";

/** Deutsche Standardsätze für die Fälle, in denen der Server keinen mitschickt. */
function standardMeldung(status: number): string {
  if (status === 0) return "Keine Verbindung zum Server. Bist du noch im Netz?";
  if (status === 400) return "Die Eingabe hat der Server nicht angenommen.";
  if (status === 401) return "Du bist nicht (mehr) angemeldet.";
  if (status === 403) return "Dazu fehlt dir die Berechtigung.";
  if (status === 404) return "Das gibt es nicht (mehr).";
  if (status === 409) return "Das wurde zwischenzeitlich geändert.";
  if (status === 413) return "Die Datei ist zu groß.";
  if (status === 415) return "Dieses Dateiformat nehmen wir nicht an.";
  if (status === 429) return "Zu viele Versuche. Bitte kurz warten.";
  if (status === 503) return "Der Server ist gerade nicht erreichbar. Gleich noch einmal versuchen.";
  if (status >= 500) return "Der Server hat einen Fehler gemeldet.";
  return `Die Anfrage ist fehlgeschlagen (${status}).`;
}

/**
 * Meldung aus dem Antwortkörper ziehen. FastAPI schreibt `detail`, unser
 * nginx-503 ebenfalls; `message` deckt alles andere ab. Ist `detail` eine
 * Liste (FastAPIs Prüffehler), wird daraus eine lesbare Zeile.
 */
function meldungAus(body: unknown, status: number): string {
  if (typeof body === "string" && body.trim() && !body.trim().startsWith("<")) return body.trim();
  if (body && typeof body === "object") {
    const o = body as Record<string, unknown>;
    if (typeof o.detail === "string" && o.detail.trim()) return o.detail.trim();
    if (typeof o.message === "string" && o.message.trim()) return o.message.trim();
    if (Array.isArray(o.detail)) {
      const teile = o.detail
        .map((d) => (d && typeof d === "object" && typeof (d as Record<string, unknown>).msg === "string" ? (d as Record<string, string>).msg : null))
        .filter(Boolean);
      if (teile.length) return teile.join(" · ");
    }
  }
  return standardMeldung(status);
}

/** Körper auswerten, ohne an einer leeren oder nicht-JSON-Antwort zu zerbrechen. */
async function koerperLesen(res: Response): Promise<unknown> {
  const typ = res.headers.get("content-type") ?? "";
  const roh = await res.text();
  if (!roh) return null;
  if (typ.includes("json")) {
    try {
      return JSON.parse(roh);
    } catch {
      return roh;
    }
  }
  return roh;
}

function adresse(pfad: string): string {
  if (pfad.startsWith("http://") || pfad.startsWith("https://")) return pfad;
  return BASIS + (pfad.startsWith("/") ? pfad : "/" + pfad);
}

export interface FetchOpts extends Omit<RequestInit, "body"> {
  /** wird als JSON gesendet; für FormData bitte postForm() nehmen */
  json?: unknown;
  /** roher Körper (FormData, Blob, string) — dann setzt diese Funktion KEIN content-type */
  body?: BodyInit | null;
}

/**
 * Eine Anfrage ans Backend. Gibt den ausgewerteten Körper zurück; bei 204
 * oder leerem Körper `undefined` (als T durchgereicht — wer nichts erwartet,
 * ruft `fetchJson<void>(…)`).
 *
 * Wirft bei jedem Status ≥ 400 und bei Netzfehlern einen ApiError mit einer
 * deutschen `message`, die direkt in einen Toast passt.
 */
export async function fetchJson<T = unknown>(pfad: string, opts: FetchOpts = {}): Promise<T> {
  const { json, body, headers, method, ...rest } = opts;
  const m = (method ?? (json !== undefined || body !== undefined ? "POST" : "GET")).toUpperCase();
  const h = new Headers(headers);
  h.set("Accept", "application/json");
  // Der CSRF-Riegel. Absichtlich an der Methode festgemacht und nicht am
  // Aufrufer: wer eine neue verändernde Route ergänzt, kann ihn nicht vergessen.
  if (VERAENDERND.has(m)) h.set("X-ZP", "1");
  if (json !== undefined) h.set("Content-Type", "application/json");
  // Bei FormData setzt der Browser den content-type selbst — mit der
  // boundary, die nur er kennt. Ein eigener Wert hier macht den Upload
  // unlesbar; darum steht hier keiner.

  const url = adresse(pfad);
  let res: Response;
  try {
    res = await fetch(url, {
      ...rest,
      method: m,
      headers: h,
      cache: "no-store",
      credentials: "same-origin",
      body: json !== undefined ? JSON.stringify(json) : (body ?? null),
    });
  } catch (e) {
    // Abbruch über AbortController ist KEIN Fehler der Anwendung — der
    // Aufrufer hat selbst abgebrochen und will seinen eigenen Fehler sehen.
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiError(0, standardMeldung(0), null, url);
  }

  if (res.status === 204 || res.status === 205) return undefined as T;

  const koerper = await koerperLesen(res);
  if (!res.ok) throw new ApiError(res.status, meldungAus(koerper, res.status), koerper, url);
  // Ein 200 OHNE JSON ist kein Erfolg, sondern ein Irrläufer: nginx liefert die
  // index.html aus, wenn /api ins Leere zeigt, und ein `r.eintraege` darauf wäre
  // `undefined` — der Bildschirm bliebe leer, ohne dass irgendwo ein Fehler steht.
  // Lieber ein ehrlicher Satz (Spezifikation 11.2 Punkt 7).
  if (typeof koerper === "string") throw new ApiError(res.status, NICHT_ERREICHBAR, koerper, url);
  return (koerper ?? undefined) as T;
}

export const getJson = <T = unknown>(pfad: string, opts: FetchOpts = {}) => fetchJson<T>(pfad, { ...opts, method: "GET" });
export const postJson = <T = unknown>(pfad: string, json?: unknown, opts: FetchOpts = {}) => fetchJson<T>(pfad, { ...opts, method: "POST", json });
export const putJson = <T = unknown>(pfad: string, json?: unknown, opts: FetchOpts = {}) => fetchJson<T>(pfad, { ...opts, method: "PUT", json });
export const patchJson = <T = unknown>(pfad: string, json?: unknown, opts: FetchOpts = {}) => fetchJson<T>(pfad, { ...opts, method: "PATCH", json });
export const deleteJson = <T = unknown>(pfad: string, opts: FetchOpts = {}) => fetchJson<T>(pfad, { ...opts, method: "DELETE" });

/**
 * Datei-Upload (multipart). Nimmt fertige FormData und setzt AUSDRÜCKLICH
 * kein Content-Type — der Browser muss die boundary selbst anhängen. Wer hier
 * "multipart/form-data" von Hand setzt, bekommt eine Anfrage, die der Server
 * nicht zerlegen kann; das ist der häufigste Upload-Fehler überhaupt.
 *
 *   const fd = new FormData();
 *   fd.append("datei", file);
 *   await postForm("/fragen/12/anhaenge", fd);
 */
export function postForm<T = unknown>(pfad: string, form: FormData, opts: FetchOpts = {}): Promise<T> {
  return fetchJson<T>(pfad, { ...opts, method: opts.method ?? "POST", body: form });
}
